import {
  Button,
  EllipsisSpan,
  FormContent,
  PopConfirm,
  Select,
  Table,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { Space, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import {
  apiV2AirStationConfigScadaListPost,
  apiV2AreaGetValveConfigListPost,
  apiV2AreaDelValvePost,
  apiV2AreaGetAllMediumValvesPost,
  apiV2AreaUpsertScadaPost,
  apiV2AreaMediumDeviceLinkPost,
} from '@maxtropy/device-customer-apis-v2';
import { ValveDevice, CommandStatus, ValveConfigData, DeviceItem } from './types';
import DeviceSelectModal from './components/DeviceSelectModal';
import StatusConfigModal from './components/StatusConfigModal';
import CommandConfigModal from './components/CommandConfigModal';
import AutoControlConfigModal from './components/AutoControlConfigModal';

const ValveConfig: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const autoControlQueryHandledRef = useRef(false);

  const [configData, setConfigData] = useState<ValveConfigData>();
  const [activeMedium, setActiveMedium] = useState<number>();
  const [selectedDevices, setSelectedDevices] = useState<DeviceItem[]>([]);
  const [deviceSelectVisible, setDeviceSelectVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState<ValveDevice>();
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [commandModalVisible, setCommandModalVisible] = useState(false);
  const [commandStatus, setCommandStatus] = useState<CommandStatus | null>();
  const [autoControlModalVisible, setAutoControlModalVisible] = useState(false);

  // 加载配置数据
  const { loading } = useRequest(() => apiV2AreaGetValveConfigListPost({ id }), {
    onSuccess: data => {
      setConfigData(data);
    },
  });

  // 获取站房组态列表
  const { data: stationList } = useRequest(() => apiV2AirStationConfigScadaListPost({}));

  // 站房组态选项
  const stationOptions = useMemo(() => {
    return (stationList?.list || []).map(item => ({
      value: item.areaId,
      label: item.airStationName,
    }));
  }, [stationList]);

  // 更新阀门列表
  const updateValveListByMediumId = (mediumId: number) => {
    apiV2AreaGetAllMediumValvesPost({ id, mediumId }).then(data => {
      setConfigData(prev => {
        if (!prev?.list) return prev;
        return {
          ...prev,
          list: prev.list.map(config => {
            if (config.mediumId === mediumId) {
              return {
                ...config,
                valveDetailList: (data?.list ?? []) as ValveDevice[],
              };
            }
            return config;
          }),
        };
      });
    });
  };

  // 确认选择设备
  const handleDeviceSelectOk = (selectedDevices: DeviceItem[]) => {
    const deviceList = selectedDevices.map(d => ({
      deviceId: d.id!,
      deviceName: d.name!,
    }));

    apiV2AreaMediumDeviceLinkPost({ id, mediumId: activeMedium, deviceList }).then(() => {
      setDeviceSelectVisible(false);
      setSelectedDevices([]);
      updateValveListByMediumId(activeMedium!);
    });
  };

  // 移除设备
  const handleRemoveDevice = (mediumId: number, deviceId: number) => {
    apiV2AreaDelValvePost({ id, mediumId, deviceId }).then(() => updateValveListByMediumId(mediumId));
  };

  // 打开设备选择弹窗
  const handleOpenDeviceSelect = (mediumId: number) => {
    setActiveMedium(mediumId);
    apiV2AreaGetAllMediumValvesPost({ id, mediumId }).then(data => {
      setSelectedDevices((data?.list ?? []).map(i => ({ id: i.deviceId!, name: i.deviceName })));
      setDeviceSelectVisible(true);
    });
  };

  // 打开配置弹窗
  const handleOpenStatusConfig = (mediumId: number, device: ValveDevice) => {
    setActiveMedium(mediumId);
    setEditingDevice(device);
    setStatusModalVisible(true);
  };

  // 打开指令配置弹窗
  const handleOpenCommandConfig = (mediumId: number, device: ValveDevice, status: CommandStatus) => {
    setActiveMedium(mediumId);
    setEditingDevice(device);
    setCommandModalVisible(true);
    setCommandStatus(status);
  };

  // 打开自动控制配置弹窗
  const handleOpenAutoControlConfig = (mediumId: number, device: ValveDevice) => {
    setActiveMedium(mediumId);
    setEditingDevice(device);
    setAutoControlModalVisible(true);
  };

  useEffect(() => {
    if (autoControlQueryHandledRef.current || !configData?.list?.length) return;

    const autoControlDeviceIdParam = searchParams.get('autoControlDeviceId');
    const autoControlMediumIdParam = searchParams.get('autoControlMediumId');
    const autoControlDeviceId = autoControlDeviceIdParam ? Number(autoControlDeviceIdParam) : undefined;
    const autoControlMediumId = autoControlMediumIdParam ? Number(autoControlMediumIdParam) : undefined;
    if (!Number.isFinite(autoControlDeviceId)) return;

    const targetMedium = configData.list.find(mediumConfig => {
      if (autoControlMediumId !== undefined && mediumConfig.mediumId !== autoControlMediumId) return false;
      return mediumConfig.valveDetailList?.some(device => device.deviceId === autoControlDeviceId);
    });
    const targetDevice = targetMedium?.valveDetailList?.find(device => device.deviceId === autoControlDeviceId);
    if (!targetMedium?.mediumId || !targetDevice) return;

    autoControlQueryHandledRef.current = true;
    handleOpenAutoControlConfig(targetMedium.mediumId, targetDevice);
  }, [configData?.list, searchParams]);

  // 表格列配置
  const getColumns = (mediumId: number) => [
    {
      title: '阀门名称',
      dataIndex: 'deviceName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={`${v}`} />,
    },
    {
      title: '阀门状态配置',
      ellipsis: { showTitle: true },
      render: (_: any, record: ValveDevice) => (
        <Button type="link" onClick={() => handleOpenStatusConfig(mediumId, record)}>
          配置
        </Button>
      ),
    },
    {
      title: '控制指令配置',
      ellipsis: { showTitle: true },
      render: (_: any, record: ValveDevice) => (
        <Space size={40}>
          <div>
            开：
            <Button type="link" onClick={() => handleOpenCommandConfig(mediumId, record, CommandStatus.OPEN)}>
              配置
            </Button>
          </div>
          <div>
            关：{' '}
            <Button type="link" onClick={() => handleOpenCommandConfig(mediumId, record, CommandStatus.CLOSE)}>
              配置
            </Button>
          </div>
        </Space>
      ),
    },
    {
      title: '自动控制配置',
      ellipsis: { showTitle: true },
      render: (_: any, record: ValveDevice) => (
        <Button type="link" onClick={() => handleOpenAutoControlConfig(mediumId, record)}>
          配置
        </Button>
      ),
    },
    {
      title: '对应站房组态',
      ellipsis: { showTitle: true },
      render: (_: any, record: ValveDevice) => (
        <Select
          value={record.scadaId}
          placeholder="请选择站房组态"
          options={stationOptions}
          showSearch
          style={{ width: '100%' }}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          onChange={value => {
            apiV2AreaUpsertScadaPost({ id, mediumId, deviceId: record.deviceId!, scadaId: value }).then(() => {
              setConfigData(prev => {
                if (!prev?.list) return prev;

                return {
                  ...prev,
                  list: prev.list.map(config => {
                    if (config.mediumId !== mediumId) return config;

                    return {
                      ...config,
                      valveDetailList: config.valveDetailList?.map(device =>
                        device.deviceId === record.deviceId ? { ...device, scadaId: value } : device
                      ),
                    };
                  }),
                };
              });
            });
          }}
        />
      ),
    },
    {
      title: '操作',
      width: 150,
      render: (_: any, record: ValveDevice) => (
        <PopConfirm title="你确定要移除吗？" onConfirm={() => handleRemoveDevice(mediumId, record.deviceId!)}>
          <Button type="link">移除</Button>
        </PopConfirm>
      ),
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '阀门配置' }]}>
      <FormContent title="阀门配置" narrow>
        <Spin spinning={loading}>
          <Space size={32} direction="vertical">
            {configData?.list?.map(mediumConfig => {
              return (
                <Space size={10} direction="vertical">
                  <div style={{ marginBottom: 6, fontSize: 16, fontWeight: 500, color: 'var(--mx-text-base-color)' }}>
                    {mediumConfig.mediumName}
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenDeviceSelect(mediumConfig.mediumId!)}
                  >
                    新增{mediumConfig.mediumName}阀门
                  </Button>
                  <Table
                    rowKey="deviceId"
                    columns={getColumns(mediumConfig.mediumId!)}
                    dataSource={mediumConfig.valveDetailList!}
                    pagination={false}
                  />
                </Space>
              );
            })}
            <Button onClick={() => navigate(-1)}>取消</Button>
          </Space>
        </Spin>
      </FormContent>
      <DeviceSelectModal
        visible={deviceSelectVisible}
        onCancel={() => {
          setDeviceSelectVisible(false);
          setSelectedDevices([]);
        }}
        onOk={handleDeviceSelectOk}
        disabledDevices={selectedDevices}
      />
      {statusModalVisible && (
        <StatusConfigModal
          visible={statusModalVisible}
          device={editingDevice}
          areaId={Number(id)}
          mediumId={activeMedium}
          onCancel={() => {
            setStatusModalVisible(false);
            setEditingDevice(undefined);
          }}
        />
      )}
      {commandModalVisible && (
        <CommandConfigModal
          visible={commandModalVisible}
          device={editingDevice!}
          areaId={Number(id)}
          mediumId={activeMedium}
          type={commandStatus!}
          onCancel={() => {
            setCommandModalVisible(false);
            setEditingDevice(undefined);
          }}
        />
      )}

      {autoControlModalVisible && (
        <AutoControlConfigModal
          visible={autoControlModalVisible}
          device={editingDevice}
          areaId={Number(id)}
          mediumId={activeMedium}
          onCancel={() => {
            setAutoControlModalVisible(false);
            setEditingDevice(undefined);
          }}
          onOk={() => {
            setAutoControlModalVisible(false);
            setEditingDevice(undefined);
          }}
        />
      )}
    </Wrapper>
  );
};

export default ValveConfig;
